import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * The container every client portal page uses. There is no second one.
 *
 * Same reasoning as `AdminPage`: in the prototype each page set its own
 * container and one of them ended up wider than the rest. One component means a
 * page cannot drift.
 *
 * No breadcrumb trail here. The portal is five destinations deep at most, and a
 * trail reading "Dashboard › My Cleans › J-2026-0004" above a screen a customer
 * reached by tapping one card is furniture. Detail pages get a single explicit
 * "back to" link instead, which is the thing people actually want.
 */
export function ClientPage({
  title,
  description,
  back,
  actions,
  children,
}: {
  title: string;
  description?: ReactNode;
  /** The one way out of a detail page. */
  back?: { to: string; label: string };
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-content px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      {back ? (
        <Link
          to={back.to}
          className="mb-3 inline-flex min-h-touch items-center gap-1 text-label text-bark-700 underline-offset-2 hover:underline"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
          {back.label}
        </Link>
      ) : null}

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-h1 text-ink-900">{title}</h1>
          {description ? <p className="mt-1 text-body text-ink-500">{description}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
      </div>

      {children}
    </div>
  );
}
